import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, panels as panelsApi, sites as sitesApi, stats as statsApi, type ModelId, type Cadence } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, ErrorBox, PageLoading } from '../components/AuthLayout'
import { MentionTrendChart, MentionBreakdownChart } from '../components/MentionCharts'
import { MultiSelect } from '../components/MultiSelect'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DateRangePicker } from '../components/DateRangePicker'

const MODEL_LABELS: Record<string, string> = { chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini' }
const MODEL_OPTIONS = Object.entries(MODEL_LABELS).map(([value, label]) => ({ value, label }))
const LANGUAGE_OPTIONS = [
  { value: 'nl', label: 'Nederlands' },
  { value: 'en', label: 'Engels' },
]

function paramList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key)
  return raw ? raw.split(',').filter(Boolean) : []
}
const RUN_STATUS_LABELS: Record<string, string> = {
  queued: 'In wachtrij',
  running: 'Bezig',
  succeeded: 'Gelukt',
  failed: 'Mislukt',
  refunded: 'Terugbetaald',
}

// The page that was missing entirely (app-shell-and-navigation plan §1b): what a site
// actually IS once it exists — its brand details, its panel (or the lack of one, with a
// way forward), and its run history, since closing the wizard's tab mid-run previously
// meant losing all access to that run.
export function SiteDetail() {
  const { id } = useParams<{ id: string }>()
  const siteId = Number(id)
  const navigate = useNavigate()
  const me = useMe()
  // retry: false, matching useMe()'s own convention — a 404 (wrong org, deleted site) is
  // not transient and retrying it just delays showing the "not found" state for no
  // benefit. Caught by a test: without this, the default retry/backoff left the page
  // stuck on a blank loading state for several seconds before ever rendering anything.
  const site = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId),
    enabled: me.isSuccess && !!siteId,
    retry: false,
  })

  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [cadenceBusy, setCadenceBusy] = useState(false)
  const [rollupBusy, setRollupBusy] = useState(false)

  async function onRollupChange(exclude: boolean) {
    setError('')
    setRollupBusy(true)
    try {
      await sitesApi.setRollup(Number(siteId), exclude)
      await queryClient.invalidateQueries({ queryKey: ['site', siteId] })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de instelling niet opslaan.')
    } finally {
      setRollupBusy(false)
    }
  }
  const queryClient = useQueryClient()

  async function onCadenceChange(panelId: number, cadence: Cadence) {
    setError('')
    setCadenceBusy(true)
    try {
      await panelsApi.setCadence(panelId, cadence)
      // Refetch rather than patching local state: the server decides the next run time,
      // and showing a guess would be showing something that is not true.
      await queryClient.invalidateQueries({ queryKey: ['site', siteId] })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de frequentie niet opslaan.')
    } finally {
      setCadenceBusy(false)
    }
  }

  const [searchParams, setSearchParams] = useSearchParams()
  const selectedModels = paramList(searchParams, 'models')
  const selectedLanguages = paramList(searchParams, 'languages')
  const selectedPrompts = paramList(searchParams, 'prompts')
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  function setFilter(key: string, values: string[]) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (values.length === 0) next.delete(key)
        else next.set(key, values.join(','))
        return next
      },
      { replace: true },
    )
  }
  function setRange(nextFrom: string, nextTo: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        nextFrom ? next.set('from', nextFrom) : next.delete('from')
        nextTo ? next.set('to', nextTo) : next.delete('to')
        return next
      },
      { replace: true },
    )
  }

  const mentionStats = useQuery({
    queryKey: ['mention-stats', siteId, selectedModels, selectedLanguages, selectedPrompts, from, to],
    queryFn: () =>
      statsApi.mentions(siteId, {
        models: selectedModels as ModelId[],
        languages: selectedLanguages,
        promptIds: selectedPrompts,
        from,
        to,
      }),
    enabled: site.isSuccess,
  })

  useEffect(() => {
    if (me.isError) navigate('/login')
  }, [me.isError, navigate])

  async function startNewRun(panelId: number) {
    setError('')
    setBusy(true)
    try {
      const started = await panelsApi.run(panelId)
      navigate(`/runs/${started.run_id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de meting niet starten.')
    } finally {
      setBusy(false)
    }
  }

  if (me.isLoading || site.isLoading) return <PageLoading />
  if (me.isError) return null

  if (site.isError) {
    return (
      <AuthLayout title="Site niet gevonden" wide>
        <ErrorBox>Deze site bestaat niet (meer), of hoort niet bij jouw organisatie.</ErrorBox>
        <Link to="/sites" className="text-amber">
          ← Terug naar sites
        </Link>
      </AuthLayout>
    )
  }
  const data = site.data
  if (!data) return null

  // Options come from the panel's own prompts, not from the stats breakdown: a prompt with
  // zero measurements in the current window must stay selectable, otherwise filtering to it
  // would make its own option disappear.
  const promptOptions = (data.panel?.prompts ?? []).map((p) => ({
    value: String(p.id),
    label: p.text.length > 60 ? `${p.text.slice(0, 60)}…` : p.text,
  }))

  return (
    <AuthLayout title={data.name} wide>
      <p className="-mt-4 mb-6 text-sm text-ink-soft">
        {data.domain}
        {data.industry && ` · ${data.industry}`}
        {data.region && ` · ${data.region}`}
      </p>
      <ErrorBox>{error}</ErrorBox>

      {!data.panel ? (
        <div className="mb-6 rounded-xl border border-line bg-card p-5">
          <p className="mb-1 text-sm text-ink-soft">Nog geen prompts ingesteld voor deze site.</p>
          <Link
            to={`/sites/${siteId}/setup`}
            className="mt-3 inline-block rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90"
          >
            Prompts instellen →
          </Link>
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-line bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-ink-soft">Huidige instelling</span>
            <Link to={`/sites/${siteId}/setup`} className="text-sm text-amber">
              Bewerken
            </Link>
          </div>
          <dl className="flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-soft">Prompts</dt>
              <dd className="text-ink">{data.panel.prompts.length}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-soft">Talen</dt>
              <dd className="text-ink">{data.panel.languages.join(', ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-soft">Modellen</dt>
              <dd className="text-ink">{data.panel.models.map((m) => MODEL_LABELS[m] ?? m).join(', ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-soft">Herhalingen</dt>
              <dd className="text-ink">{data.panel.repeats}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-soft">Meetellen in totalen</dt>
              <dd>
                {/* For the site you add to check the product works at all: its brand is
                    certain to be named, so it reports 100% and that 100% is averaged into
                    every org-wide figure. Deleting it loses the check; leaving it in makes
                    the headline a lie. */}
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!data.exclude_from_rollup}
                    disabled={rollupBusy}
                    onChange={(e) => onRollupChange(!e.target.checked)}
                    className="h-4 w-4 accent-amber"
                  />
                  <span className="text-xs text-ink-soft">
                    {data.exclude_from_rollup ? 'Alleen op deze pagina' : 'Ook in het dashboard'}
                  </span>
                </label>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-soft">Automatisch meten</dt>
              <dd>
                <select
                  aria-label="Automatisch meten"
                  value={data.panel.cadence}
                  disabled={cadenceBusy}
                  onChange={(e) => onCadenceChange(data.panel!.id, e.target.value as Cadence)}
                  className="rounded-lg border border-line bg-card px-2 py-1 text-ink focus:outline-none focus:ring-2 focus:ring-amber disabled:opacity-50"
                >
                  <option value="off">Uit</option>
                  <option value="daily">Dagelijks</option>
                  <option value="weekly">Wekelijks</option>
                  <option value="monthly">Maandelijks</option>
                </select>
              </dd>
            </div>
            {data.panel.cadence !== 'off' && data.panel.next_run_at && (
              <div className="flex justify-between">
                <dt className="text-ink-soft">Volgende meting</dt>
                <dd className="text-ink">
                  {new Date(data.panel.next_run_at).toLocaleString('nl-NL')}
                </dd>
              </div>
            )}
          </dl>

          {/* The only signal a customer gets that their trend has stopped. Left as a gap in
              the chart it looks like nothing happened, which is exactly what it looks like
              when everything is fine. */}
          {data.panel.last_skipped_at && (
            <div className="mt-3 rounded-lg border border-amber bg-amber/10 p-3 text-sm">
              <strong>Meting overgeslagen.</strong> Op{' '}
              {new Date(data.panel.last_skipped_at).toLocaleString('nl-NL')} was er te weinig
              saldo om te meten.{' '}
              <Link to="/credits" className="text-amber underline">
                Credits bijvullen
              </Link>
              .
            </div>
          )}
          <ConfirmDialog
            trigger={
              <button
                type="button"
                disabled={busy}
                className="mt-4 rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
              >
                {busy ? 'Bezig…' : 'Nieuwe meting starten'}
              </button>
            }
            title="Nieuwe meting starten"
            description={`Kost ~${data.run_estimate?.estimated_credits ?? '?'} credits. Doorgaan?`}
            confirmLabel="Starten"
            busy={busy}
            onConfirm={() => data.panel && startNewRun(data.panel.id)}
          />
        </div>
      )}

      {mentionStats.data && mentionStats.data.daily.some((d) => d.total > 0) && (
        <div className="mb-6 rounded-xl border border-line bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-ink-soft">
              {from && to ? `Vermeldingen (${from} t/m ${to})` : 'Vermeldingen (laatste 30 dagen)'}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <MultiSelect label="Modellen" options={MODEL_OPTIONS} selected={selectedModels} onChange={(v) => setFilter('models', v)} />
              <MultiSelect label="Talen" options={LANGUAGE_OPTIONS} selected={selectedLanguages} onChange={(v) => setFilter('languages', v)} />
              {promptOptions.length > 0 && (
                <MultiSelect label="Prompts" options={promptOptions} selected={selectedPrompts} onChange={(v) => setFilter('prompts', v)} />
              )}
              <DateRangePicker from={from} to={to} onChange={setRange} />
            </div>
          </div>
          {/* Per-model comparison lives IN the trend chart now (one line per model, click
              a legend entry to toggle it) — see Dashboard.tsx's identical reasoning. */}
          <MentionTrendChart daily={mentionStats.data.daily} />
          {mentionStats.data.by_prompt && mentionStats.data.by_prompt.length > 0 && (
            <div className="mt-4 border-t border-line pt-4">
              <div className="mb-2 text-sm text-ink-soft">Per prompt</div>
              <MentionBreakdownChart rows={mentionStats.data.by_prompt} />
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Metingen</div>
        {(data.runs ?? []).length === 0 ? (
          <p className="text-ink-soft">Nog geen metingen.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="border-b border-line pb-2">Datum</th>
                <th className="border-b border-line pb-2">Status</th>
                <th className="border-b border-line pb-2">Genoemd</th>
                <th className="border-b border-line pb-2">Metingen</th>
                <th className="border-b border-line pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run) => (
                <tr key={run.id}>
                  <td className="border-b border-line py-2">{new Date(run.created_at).toLocaleString('nl-NL')}</td>
                  <td className="border-b border-line py-2">
                    {RUN_STATUS_LABELS[run.status] ?? run.status}
                    {/* A shrunk run measured fewer prompts, and the ones dropped are the
                        lowest-ranked, which are the ones the brand is least likely to
                        appear in. Unlabelled it reads as an improvement. */}
                    {run.limited_by_credits && (
                      <span
                        title="Beperkt door saldo: minder prompts gemeten, niet vergelijkbaar met een volledige meting"
                        className="ml-2 rounded bg-amber/20 px-1.5 py-0.5 text-xs text-amber"
                      >
                        beperkt
                      </span>
                    )}
                  </td>
                  <td className="border-b border-line py-2">
                    {run.measured > 0 ? (
                      <span className="text-ink">
                        {run.mentioned} / {run.measured}
                        <span className="ml-1.5 text-ink-soft">
                          ({Math.round((run.mentioned / run.measured) * 100)}%)
                        </span>
                      </span>
                    ) : (
                      <span className="text-ink-soft">—</span>
                    )}
                  </td>
                  <td className="border-b border-line py-2 text-ink-soft">
                    {run.turns_completed ?? 0} / {run.turns_planned}
                  </td>
                  <td className="border-b border-line py-2 text-right">
                    <Link
                      to={`/runs/${run.id}`}
                      className="rounded-lg border border-line px-2.5 py-1 text-xs text-ink-soft hover:border-amber hover:text-amber"
                    >
                      Bekijken
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AuthLayout>
  )
}
